import { FC, useEffect, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { IonButton } from '@ionic/react';
import { useHistory } from 'react-router';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useSubscription } from '@meraki-internal/state';
import { CareMapState } from '../../caremap/CareMapState';
import { BottomSheetModal } from '../../components/BottomSheetModal';
import { ModalHeader } from '../../components/ModalHeader';
import { SharingAndAccessContent } from './SharingAndAccessContent';
import { AddPersonFlow } from './AddPersonFlow';

const useStyles = createUseStyles({
    sheetWrap: {
        display: 'flex',
        flexDirection: 'column',
        maxHeight: 'min(90vh, 760px)',
        background: '#fff'
    },
    scrollArea: {
        padding: '20px 22px 16px',
        overflowY: 'auto',
        flex: 1
    },
    // Pinned action footer: a sibling of the scroll area (not sticky-inside it) so the
    // scrolling content — including the visibility toggle's checkboxes — never bleeds over it.
    actionFooter: {
        flexShrink: 0,
        background: '#fff',
        padding: '12px 22px',
        borderTop: '1px solid var(--ion-color-step-100)'
    }
});

export interface ISharingAndAccessSheetProps {
    isOpen: boolean;
    // Which page to show when the sheet opens; an invite CTA can deep-link straight to 'add-person'.
    initialPage?: 'list' | 'add-person';
    // The invite flow on its own: finishing or cancelling it closes the sheet rather than
    // showing the list, for a caller whose page already shows the list.
    inviteOnly?: boolean;
    onDidDismiss: () => void;
}

export const SharingAndAccessSheet: FC<ISharingAndAccessSheetProps> = ({ isOpen, initialPage = 'list', inviteOnly = false, onDidDismiss }) => {
    const classes = useStyles();
    const history = useHistory();

    const careMapState = useInstance(CareMapState);
    useSubscription(() => careMapState);

    const [page, setPage] = useState<'list' | 'add-person'>('list');

    // Apply the requested initial page each time the sheet opens (handleClose resets to 'list').
    useEffect(() => {
        if (isOpen) {
            setPage(initialPage);
        }
    }, [isOpen, initialPage]);

    const patient = careMapState.getActiveCaremap()?.patient;

    if (!patient) {
        return null;
    }

    const handleClose = () => {
        setPage('list');
        onDidDismiss();
    };

    const leaveAddPerson = inviteOnly ? handleClose : () => setPage('list');

    // Close the sheet first, then navigate once its modal has dismissed — otherwise the
    // drawer mounts under the open modal and the caremap context is briefly unavailable.
    const closeThenNavigate = (path: string) => {
        handleClose();
        setTimeout(() => history.push(path), 350);
    };

    return (
        <BottomSheetModal isOpen={isOpen} onDidDismiss={handleClose} backdropDismiss={false} data-id="sharing-and-access-sheet">
            <div className={classes.sheetWrap}>
                {page === 'list' && (
                    <>
                        <ModalHeader title="Sharing & Access" closeHandler={handleClose} />
                        <div className={classes.scrollArea}>
                            <SharingAndAccessContent navigate={closeThenNavigate} />
                        </div>
                        <div className={classes.actionFooter}>
                            <IonButton expand="block" onClick={() => setPage('add-person')} data-id="add-person-btn">
                                Invite Someone
                            </IonButton>
                        </div>
                    </>
                )}

                {page === 'add-person' && (
                    <>
                        <ModalHeader title="Invite Someone" backHandler={inviteOnly ? undefined : leaveAddPerson} closeHandler={handleClose} />
                        <AddPersonFlow
                            onCancel={leaveAddPerson}
                            onDone={leaveAddPerson}
                            onClose={handleClose}
                            patientFirstName={patient.firstName || 'them'}
                        />
                    </>
                )}
            </div>
        </BottomSheetModal>
    );
};
